/**
 * useCachedImage — offline-first photo blob cache (L1 Dexie layer).
 *
 * On first call for a given URL:
 *   1. Checks `db.photoBlobs` (Dexie v6 store) — HIT → create object URL from cached blob.
 *   2. Cache MISS → fetch(url, { mode: 'cors' }) → store blob in Dexie → create object URL.
 *   3. Any error (offline, CORS, 4xx) → return null; Avatar falls back to initials.
 *
 * On subsequent calls (same URL, any component instance, across page refreshes):
 *   • Always served from Dexie — zero network requests.
 *
 * Object URL lifecycle:
 *   • Created only when `!cancelled` (prevents leaks on rapid URL changes).
 *   • Revoked on unmount and on url prop change (cleanup function).
 *
 * `blob:` URLs bypass the Service Worker interceptor entirely (same-origin by spec),
 * so this cache is independent of SW quota / eviction and is fully deterministic.
 */

import { useEffect, useRef, useState } from 'react';
import { db } from '@/shared/db/database';
import { capturedFetch } from '@/shared/devtools/capturedFetch';

// ── Public contract ───────────────────────────────────────────────────────────

export interface CachedImageResult {
  /** blob: object URL when the image is available; null while loading or on miss. */
  src: string | null;
  /** true while the initial Dexie lookup + optional network fetch are in flight. */
  isLoading: boolean;
}

/**
 * @param url - Canonical remote URL (e.g. "https://api.../file/photo.jpg").
 *              Pass null / undefined to get { src: null, isLoading: false } immediately.
 */
export function useCachedImage(url: string | null | undefined): CachedImageResult {
  const [src, setSrc]               = useState<string | null>(null);
  const [isLoading, setIsLoading]   = useState(false);

  // Holds the currently active object URL so the cleanup can revoke it.
  // Using a ref (not state) because revocation is a side-effect, not a render concern.
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // ── No URL provided ───────────────────────────────────────────────────────
    if (!url) {
      setSrc(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    // ── Async load ────────────────────────────────────────────────────────────
    const load = async () => {
      setIsLoading(true);

      try {
        // Step 1: Dexie cache lookup (L1)
        const cached = await db.photoBlobs.get(url);
        let blob: Blob;

        if (cached) {
          // HIT — serve from local storage, zero network traffic
          blob = cached.blob;
        } else {
          // MISS — fetch from network with CORS mode
          const response = await capturedFetch(url, { mode: 'cors' });
          blob = await response.blob();

          // Persist for all future loads (including page refreshes)
          await db.photoBlobs.put({ url, blob, fetchedAt: Date.now() });
        }

        // Guard: if the effect was already cleaned up (url changed / unmounted),
        // do NOT create a new object URL — it would leak with nothing to revoke it.
        if (!cancelled) {
          const blobUrl = URL.createObjectURL(blob);
          objectUrlRef.current = blobUrl;
          setSrc(blobUrl);
          setIsLoading(false);
        }
      } catch {
        // Network error, CORS failure, offline cache miss → null (no crash, no throw).
        // Avatar will fall back to initials text.
        if (!cancelled) {
          setSrc(null);
          setIsLoading(false);
        }
      }
    };

    load();

    // ── Cleanup ───────────────────────────────────────────────────────────────
    // Runs when: (a) url prop changes, OR (b) component unmounts.
    return () => {
      cancelled = true;

      // Revoke the active object URL to release browser memory.
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [url]);

  return { src, isLoading };
}
